/**
 * SplitLayout — VS Code-style split with two independent pane columns.
 *
 * When no secondary group exists, renders children normally (single column).
 * When split, renders two columns side-by-side, separated by a draggable
 * resize handle. Only the side pane has a tab row; the primary pane has none.
 *
 * Children are the primary pane's WorkspaceUrlSync + PrimaryPane. Surface identity
 * is NOT provided here: every tab is mounted at once now, so each one carries
 * its own — a store keyed by surface would otherwise give every primary tab the
 * active tab's id.
 */
import React, { useRef } from 'react';

import { SecondaryPane } from './SecondaryPane';
import { useSecondaryPaneVisible } from '../hooks/useSecondaryPaneVisible';
import { useSplitResize } from '../hooks/useSplitResize';
import { useWorkspaceStore } from '../stores/useWorkspaceStore';

interface Props {
  children: React.ReactNode;
}

export const SplitLayout: React.FC<Props> = ({ children }) => {
  const splitRatio = useWorkspaceStore(s => s.splitRatio);
  const focusedGroup = useWorkspaceStore(s => s.focusedGroup);
  const setFocusedGroup = useWorkspaceStore(s => s.setFocusedGroup);
  const secondaryVisible = useSecondaryPaneVisible();
  const containerRef = useRef<HTMLDivElement>(null);
  const { onMouseDown } = useSplitResize(containerRef);

  // False when there is no split at all, on a phone, and when the persisted side
  // pane holds another app's page — see useSecondaryPaneVisible.
  if (!secondaryVisible) {
    return <>{children}</>;
  }

  const primaryPercent = `${(splitRatio * 100).toFixed(1)}%`;
  const secondaryPercent = `${((1 - splitRatio) * 100).toFixed(1)}%`;

  return (
    <div ref={containerRef} className="flex min-h-0 flex-1 overflow-hidden">
      {/* Primary pane — split ratio at the nav breakpoint and above */}
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
      <div
        className={`flex min-w-0 flex-1 flex-col overflow-hidden nav:flex-none ${focusedGroup === 'primary' ? '' : 'opacity-95'}`}
        style={{ width: primaryPercent }}
        onClick={() => setFocusedGroup('primary')}
      >
        {children}
      </div>

      {/* Resize handle — only where the second column exists */}
      <button
        type="button"
        aria-label="Resize split panes"
        className="bg-border hover:bg-primary/30 active:bg-primary/50 hidden shrink-0 cursor-col-resize border-none p-0 transition-colors nav:block"
        style={{ width: 4 }}
        onMouseDown={onMouseDown}
      />

      {/* Secondary pane — gated on the same nav breakpoint as the rest of the desktop chrome,
          so 768–899px is single-column like every other phone width */}
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
      <div
        data-testid="split-secondary-column"
        className={`hidden min-w-0 flex-col overflow-hidden nav:flex ${focusedGroup === 'secondary' ? '' : 'opacity-95'}`}
        style={{ width: secondaryPercent }}
        onClick={() => setFocusedGroup('secondary')}
      >
        <SecondaryPane />
      </div>
    </div>
  );
};
